if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
        if (this.submitButton.disabled) return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.disabled = true;
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        
        // Debug logging
        console.log('Form data being sent:', Object.fromEntries(formData));
        console.log('Variant ID:', formData.get('id'));
        console.log('Quantity:', formData.get('quantity'));
        
        // Validate variant ID before submitting
        const variantId = formData.get('id');
        if (!variantId || variantId === 'undefined' || variantId === 'null') {
          console.error('Invalid variant ID:', variantId);
          this.handleErrorMessage('Please select a product variant');
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButton.disabled = false;
          this.submitButton.classList.remove('loading');
          this.querySelector('.loading__spinner').classList.add('hidden');
          return;
        }
        
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then(async (response) => {
            if (!response.ok) {
              // Get detailed error information
              let errorDetails = '';
              try {
                const errorData = await response.json();
                errorDetails = JSON.stringify(errorData);
                console.error('Detailed error response:', errorData);
              } catch (e) {
                errorDetails = 'Could not parse error response';
              }
              throw new Error(`HTTP ${response.status}: ${response.statusText} - ${errorDetails}`);
            }
            return response.json();
          })
          .then((response) => {
            if (response.status) {
              console.error('Cart add error:', response);
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              // Show success message and open cart drawer
              this.submitButton.querySelector('span').textContent = 'Added to cart!';
              
              // Fade button to black
              this.submitButton.style.backgroundColor = '#000000';
              this.submitButton.style.color = '#ffffff';
              this.submitButton.style.transition = 'all 0.3s ease';
              
              // Update cart count in header
              this.updateCartCount();
              
              // Open cart drawer and refresh its contents
              const cartDrawer = document.querySelector('cart-drawer');
              if (cartDrawer) {
                // Fetch updated cart drawer section and replace the existing one
                fetch(`${routes.cart_url}?sections=cart-drawer`)
                  .then((response) => response.json())
                  .then((sections) => {
                    const html = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
                    const newCartDrawer = html.querySelector('cart-drawer');
                    const existingCartDrawer = document.querySelector('cart-drawer');
                    if (existingCartDrawer && newCartDrawer) {
                      existingCartDrawer.replaceWith(newCartDrawer);
                    }
                    // Open the new cart drawer
                    const updatedCartDrawer = document.querySelector('cart-drawer');
                    if (updatedCartDrawer) {
                      updatedCartDrawer.open();
                    }
                  })
                  .catch((e) => {
                    console.error('Error updating cart drawer:', e);
                    // Fallback: just open the existing drawer
                    cartDrawer.open();
                  });
              }
              
              setTimeout(() => {
                // Fade back to original colors
                this.submitButton.style.backgroundColor = '';
                this.submitButton.style.color = '';
                this.submitButton.querySelector('span').textContent = window.variantStrings.addToCart;
                this.submitButton.removeAttribute('aria-disabled');
                this.submitButton.disabled = false;
              }, 2000);
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error('Cart add failed:', e);
            console.error('Form data:', Object.fromEntries(formData));
            
            // Show error message to user
            this.submitButton.querySelector('span').textContent = 'Error adding to cart';
            this.submitButton.style.backgroundColor = '#dc2626';
            this.submitButton.style.color = '#ffffff';
            
            // Reset button after 3 seconds
            setTimeout(() => {
              this.submitButton.style.backgroundColor = '';
              this.submitButton.style.color = '';
              this.submitButton.querySelector('span').textContent = window.variantStrings.addToCart;
              this.submitButton.removeAttribute('aria-disabled');
              this.submitButton.disabled = false;
            }, 3000);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) {
              this.submitButton.removeAttribute('aria-disabled');
              this.submitButton.disabled = false;
            }
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      async updateCartCount() {
        try {
          const response = await fetch('/cart.js');
          const cart = await response.json();
          
          // Update cart count in header
          const cartCountBadge = document.getElementById('cart-count-badge');
          if (cartCountBadge) {
            if (cart.item_count > 0) {
              cartCountBadge.textContent = cart.item_count < 100 ? cart.item_count : '99+';
              cartCountBadge.style.display = 'flex';
            } else {
              cartCountBadge.style.display = 'none';
            }
          }
        } catch (error) {
          console.error('Error updating cart count:', error);
        }
      }
    }
  );

}
